import { Box, Button, IconButton, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { API_GET, API_POST, DBRequest, useConfig } from '../../api/api';
import { useTranslation } from 'react-i18next';
import NoDataAnimation from '../animation/NoDataAnimation';
import { BillingPosition } from '../special/BillingPosition';
import FormFields from '../form/FormFields';
import * as Yup from 'yup';
import Loader from '../special/Loader';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import IndeterminateCheckBoxIcon from '@mui/icons-material/IndeterminateCheckBox';
import moment from 'moment';
import DateRangeIcon from '@mui/icons-material/DateRange';
import MoreTimeIcon from '@mui/icons-material/MoreTime';
import ReceiptIcon from '@mui/icons-material/Receipt';
import CalculateIcon from '@mui/icons-material/Calculate';
import MoneyLabel from '../label/MoneyLabel';
import UserStack from '../exo/UserStack';
import { renderHours } from '../../scenes/time-tracking/worktime';
import TimeEntryReviewStep from './TimeEntryReviewStep';

const SelectTimeBilling = ({
  onAddPositions,
  onClose = () => {},
  initialProject,
}) => {
  const config = useConfig();
  const { t } = useTranslation();

  // Step management: 'selection', 'review' (CHR), or 'positionsReview' (Positions)
  const [step, setStep] = useState('selection');

  const [apiData, setApiData] = useState(null);
  const [wagePresetsData, setWagePresetsData] = useState(null);
  const [dataCollection, setDataCollection] = useState(() => ({
    startDate: '',
    endDate: '',
    groupedBy: 'Position',
    billingMode: 'chr',
    rounding: '',
    ...(initialProject ? { project: initialProject } : {}),
  }));

  // Update rounding default from config when it becomes available
  useEffect(() => {
    const configRounding = config?.invoice?.timeBillingRounding;
    if (configRounding && dataCollection.rounding === '') {
      setDataCollection((prev) => ({
        ...prev,
        rounding: configRounding,
      }));
    }
  }, [config?.invoice?.timeBillingRounding]);

  // Auto-fetch data when project is pre-selected
  useEffect(() => {
    if (initialProject && config) {
      handleChange({ project: initialProject });
    }
  }, [initialProject, config]);

  const [workPackageIds, setWorkPackageIds] = useState([]);
  const [selectedWagePresetIds, setSelectedWagePresetIds] = useState([]);
  const [isValid, setIsValid] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  function handleChange(insert) {
    const newDataCollection = { ...dataCollection, ...insert };
    setDataCollection(newDataCollection);

    // Reset selections when billing mode changes
    if (insert.billingMode) {
      setWorkPackageIds([]);
      setSelectedWagePresetIds([]);
      setApiData(null);
      setWagePresetsData(null);
    }

    if (!newDataCollection.project) {
      setApiData(null);
      setWagePresetsData(null);
      setWorkPackageIds([]);
      setSelectedWagePresetIds([]);
      return;
    }

    // Use default date range if not specified (entire history)
    const startDate = newDataCollection.startDate || '1970-01-01';
    const endDate = newDataCollection.endDate || moment().format('YYYY-MM-DD');

    const isChrMode = newDataCollection.billingMode === 'chr';

    if (isChrMode) {
      // Fetch wage presets for CHR mode
      DBRequest({
        config,
        path: `projects/${newDataCollection.project.id}/wage-presets?startDate=${startDate}&endDate=${endDate}`,
        method: API_GET,
        onResponse: setWagePresetsData,
        onLoading: setIsLoading,
      });
    } else {
      // Fetch billable positions for regular mode
      DBRequest({
        config,
        path: `project-positions?projectId[eq]=${newDataCollection.project.id}&startDate=${startDate}&endDate=${endDate}`,
        method: API_GET,
        onResponse: setApiData,
        onLoading: setIsLoading,
      });
    }
  }

  function handleSubmit() {
    const { billingMode } = dataCollection;
    const isChrMode = billingMode === 'chr';

    if (isChrMode) {
      // For CHR mode, go to review step first
      setStep('review');
    } else {
      // For positions mode, also go to review step to allow time adjustments
      setStep('positionsReview');
    }
  }

  // Called when review step confirms with adjustments and calculated hours
  function handleReviewConfirm(adjustments, presetHoursMap) {
    const startDate = dataCollection.startDate || '1970-01-01';
    const endDate = dataCollection.endDate || moment().format('YYYY-MM-DD');
    const selectedPresets = wagePresetsData?.data?.filter((wp) =>
      selectedWagePresetIds.includes(wp.id),
    );
    generateChrPositions(
      selectedPresets,
      startDate,
      endDate,
      adjustments,
      presetHoursMap,
    );
  }

  // Build presets from positions mode work packages for the review step
  function buildPositionsPresets() {
    if (!apiData?.data) return [];

    const presets = [];
    apiData.data.forEach((position) => {
      const { workPackages } = position;
      workPackages.forEach((wp) => {
        if (workPackageIds.includes(wp.id)) {
          presets.push({
            id: wp.id,
            label: wp.title || wp.name || position.name,
            // Use position price as hourly rate for amount calculation
            wage: position.price || 0,
            color: position.color || '#2196f3',
            timeTrackingIds: wp.timeTrackingIds || [],
            workedTimeSeconds: wp.workedTimeSeconds || 0,
            billedTimeSeconds: wp.billedTimeSeconds || 0,
          });
        }
      });
    });
    return presets;
  }

  // Called when positions review step confirms with adjustments
  function handlePositionsReviewConfirm(
    adjustments,
    positionHoursMap,
    timeTrackingDurations,
  ) {
    const startDate = dataCollection.startDate || '1970-01-01';
    const endDate = dataCollection.endDate || moment().format('YYYY-MM-DD');
    const { groupedBy } = dataCollection;

    // Call API to get grouped positions, then apply adjustments
    const reqData = {
      startDate,
      endDate,
      workPackageIds,
      groupedBy,
    };

    DBRequest({
      config,
      path: 'work-packages/billable-time',
      method: API_POST(reqData),
      isLoading: setIsLoading,
      onResponse: (response) =>
        generatePositionsWithAdjustments(
          response,
          adjustments,
          positionHoursMap,
          timeTrackingDurations,
        ),
    });
  }

  // Round hours up to the specified interval
  function roundHoursUp(hours, rounding) {
    if (rounding === 'none' || !rounding) return hours;
    const intervalMinutes = parseInt(rounding, 10);
    const intervalHours = intervalMinutes / 60;
    return Math.ceil(hours / intervalHours) * intervalHours;
  }

  function generateChrPositions(
    selectedPresets,
    startDate,
    endDate,
    adjustments = {},
    presetHoursMap = null,
  ) {
    const positions = [];
    const format = config.general.dateOutputFormat;
    const { rounding } = dataCollection;
    const formattedStartDate = moment(startDate).format(format);
    const formattedEndDate = moment(endDate).format(format);

    // Check if startDate is the epoch fallback (no start date selected)
    const isEpochStart =
      startDate === '1970-01-01' || startDate?.startsWith('1970-01-01');

    // Collect all time tracking IDs across all presets for invoice-level data
    const allTimeTrackingIds = [];
    const wagePresetsForConfig = [];

    selectedPresets?.forEach((preset) => {
      let openHours;
      let hoursAlreadyRounded = false;

      // If we have calculated hours from the review step, use those (already rounded)
      if (presetHoursMap && presetHoursMap[preset.id] !== undefined) {
        openHours = presetHoursMap[preset.id];
        hoursAlreadyRounded = true; // presetHoursMap values are already rounded in the review step
      } else {
        // Fallback to original calculation
        const workedSeconds = preset.workedTimeSeconds || 0;
        const billedSeconds = preset.billedTimeSeconds || 0;
        const openSeconds = workedSeconds - billedSeconds;
        openHours = Math.max(0, openSeconds / 3600);
      }

      const presetTimeTrackingIds = preset.timeTrackingIds || [];

      // Skip only if there are no time tracking IDs at all
      if (presetTimeTrackingIds.length === 0) return;

      // Collect all time tracking IDs
      allTimeTrackingIds.push(...presetTimeTrackingIds);

      // Apply rounding only if not already rounded from review step
      const roundedHours = hoursAlreadyRounded
        ? openHours
        : openHours > 0
          ? roundHoursUp(openHours, rounding)
          : 0;

      // Add preset to billing config with calculated hours
      wagePresetsForConfig.push({
        id: preset.id,
        label: preset.label,
        wage: preset.wage,
        hours: parseFloat(roundedHours.toFixed(2)),
        timeTrackingIds: presetTimeTrackingIds,
      });

      // Build position name: if no start date, show "bis EndDate", otherwise "StartDate bis EndDate"
      const positionName = isEpochStart
        ? `${preset.label} - ${t('until')} ${formattedEndDate}`
        : `${preset.label} - ${formattedStartDate} ${t('until')} ${formattedEndDate}`;

      positions.push({
        name: positionName,
        amount: parseFloat(roundedHours.toFixed(2)),
        unit: 'h',
        price: preset.wage,
        taxRate: config.general.defaultTaxRate || 19,
        positionableId: null,
        positionableType: 'custom',
        taskTitle: '',
        pricePerHour: preset.wage,
        monthlyLimit: 0,
        status: 0,
        hoaiId: null,
        isGroup: false,
        dateRangeStart: isEpochStart ? null : startDate,
        dateRangeEnd: endDate,
        description: '',
        depth: 0,
        type: 'default',
        wagePresetId: preset.id,
        // Keep for backward compatibility with position-level processing
        timeTrackingIds: presetTimeTrackingIds,
        timeTrackingAdjustments: null, // Moved to invoice level
      });
    });

    // Build invoice-level time tracking data (new architecture)
    const timeTrackingData = {
      ids: [...new Set(allTimeTrackingIds)], // Deduplicate IDs
      adjustments: adjustments,
      billingConfig: {
        mode: 'chr',
        rounding: rounding || null,
        startDate: isEpochStart ? null : startDate,
        endDate: endDate,
        wagePresets: wagePresetsForConfig,
      },
    };

    onAddPositions(positions, timeTrackingData);
    onClose();
  }

  // Generate positions with adjustments from review step
  function generatePositionsWithAdjustments(
    data,
    adjustments = {},
    positionHoursMap = {},
    timeTrackingDurations = {},
  ) {
    const positions = [];
    const format = config.general.dateOutputFormat;
    const { project, rounding } = dataCollection;

    // Collect all time tracking IDs for invoice-level data
    const allTimeTrackingIds = [];

    data.forEach((group) => {
      const { startDate, endDate } = group;
      const formattedStartDate = moment(startDate).format(format);
      const formattedEndDate = moment(endDate).format(format);

      // Check if startDate is the epoch fallback (no start date selected)
      const isEpochStart =
        startDate === '1970-01-01' || startDate?.startsWith('1970-01-01');

      const isSameDay = formattedStartDate === formattedEndDate;
      const projectName = project?.label || project?.name || project?.nr || '';

      // Build label: if no start date, show "bis EndDate", otherwise "StartDate bis EndDate"
      let groupLabel;
      if (isEpochStart) {
        groupLabel = `${projectName} - ${t('billing')} ${t('until')} ${formattedEndDate}`;
      } else if (isSameDay) {
        groupLabel = `${projectName} - ${t('billing for')} ${formattedStartDate}`;
      } else {
        groupLabel = `${projectName} - ${t('billing for')} ${formattedStartDate} ${t(
          'until',
        )} ${formattedEndDate}`;
      }

      positions.push({
        name: groupLabel,
        amount: 0,
        unit: '',
        price: 0,
        taxRate: 0,
        positionableId: null,
        positionableType: 'custom',
        taskTitle: '',
        pricePerHour: '',
        monthlyLimit: 0,
        status: 0,
        hoaiId: null,
        isGroup: true,
        dateRangeStart: isEpochStart ? null : startDate,
        dateRangeEnd: endDate,
        depth: 0,
        type: 'default',
      });

      // create positions
      group.positions.forEach((pos) => {
        const {
          amount,
          description,
          name,
          price,
          taxRate,
          timeTrackingIds,
          unit,
          type,
          image,
          timeTrackingComments,
          users,
          serviceId,
          workPackageId,
        } = pos;

        // Collect time tracking IDs
        if (timeTrackingIds && timeTrackingIds.length > 0) {
          allTimeTrackingIds.push(...timeTrackingIds);
        }

        const unitToUse = type === 'time' ? 'h' : unit;

        // Use per-time-tracking durations from review step if available.
        // This correctly handles day/time grouping by summing only the
        // durations of time entries belonging to THIS position,
        // and excludes hidden (excluded from report) entries (duration = 0).
        let finalAmount = amount;
        const hasTrackingDurations =
          Object.keys(timeTrackingDurations).length > 0;
        if (type === 'time' && timeTrackingIds && hasTrackingDurations) {
          finalAmount = timeTrackingIds.reduce((sum, ttId) => {
            const duration = timeTrackingDurations[ttId];
            return sum + (duration !== undefined ? duration : 0);
          }, 0);
        } else if (
          type === 'time' &&
          workPackageId &&
          positionHoursMap[workPackageId] !== undefined
        ) {
          finalAmount = positionHoursMap[workPackageId];
        }

        // Apply rounding for time-based positions
        finalAmount =
          type === 'time'
            ? parseFloat(roundHoursUp(finalAmount, rounding).toFixed(2))
            : finalAmount;

        const userDescription = generateDescription(
          timeTrackingComments,
          users,
        );

        // Set positionable fields based on whether a service is linked
        const positionableType = serviceId ? 'service' : 'custom';
        const positionableId = serviceId || null;

        positions.push({
          timeTrackingIds,
          name,
          amount: finalAmount,
          unit: unitToUse,
          price,
          taxRate,
          positionableId,
          positionableType,
          taskTitle: '',
          pricePerHour: '',
          monthlyLimit: 0,
          status: 0,
          hoaiId: null,
          isGroup: false,
          dateRangeStart: isEpochStart ? null : startDate,
          dateRangeEnd: endDate,
          description: (description || ' ') + userDescription,
          depth: 1,
          image,
          type: 'default',
        });
      });
    });

    // Build wage presets from time-based positions for timesheet hourly rate display
    const wagePresetsFromPositions = [];
    data.forEach((group) => {
      group.positions.forEach((pos) => {
        if (pos.type === 'time' && pos.timeTrackingIds?.length > 0) {
          wagePresetsFromPositions.push({
            wage: pos.price,
            timeTrackingIds: pos.timeTrackingIds,
          });
        }
      });
    });

    // Build invoice-level time tracking data WITH adjustments
    // Use null for startDate if no start date was selected (rather than epoch date)
    const positionsStartDate = dataCollection.startDate || null;
    const positionsEndDate =
      dataCollection.endDate || moment().format('YYYY-MM-DD');

    const timeTrackingData =
      allTimeTrackingIds.length > 0
        ? {
            ids: [...new Set(allTimeTrackingIds)], // Deduplicate IDs
            adjustments: adjustments, // Include adjustments from review step
            billingConfig: {
              mode: 'positions',
              rounding: rounding || null,
              startDate: positionsStartDate,
              endDate: positionsEndDate,
              wagePresets: wagePresetsFromPositions,
            },
          }
        : null;

    onAddPositions(positions, timeTrackingData);
    onClose();
  }

  function generateDescription(timeTrackingComments, users) {
    var userComment = '';

    // map user to id
    var userMap = {};
    users.forEach((user) => {
      userMap[user.id] = user;
    });

    // generate comments
    timeTrackingComments.forEach((element) => {
      const { comment, userId } = element;
      if (!comment) return;
      const user = userMap[userId];
      userComment += `<p><strong class="ql-size-12px" style="font-size:12px">${user.salutation} ${user.lastName}</strong></p>${comment}`;
    });

    return userComment;
  }

  const fields = [
    {
      label: t('Billing Mode'),
      key: 'billingMode',
      type: 'smallIconCards',
      size: 'wide',
      colspan: 2,
      cards: [
        {
          Icon: ReceiptIcon,
          label: t('Billable Positions'),
          value: 'positions',
        },
        {
          Icon: CalculateIcon,
          label: t('Calculative Hourly Rates'),
          value: 'chr',
        },
      ],
    },
    {
      label: t('position structure'),
      key: 'groupedBy',
      type: 'smallIconCards',
      size: 'wide',
      colspan: 2,
      hidden: dataCollection.billingMode === 'chr',
      cards: [
        {
          Icon: DateRangeIcon,
          label: t('Any Time'),
          value: 'Time',
        },
        {
          Icon: MoreTimeIcon,
          label: t('Daily Basis'),
          value: 'Day',
        },
        {
          Icon: DateRangeIcon,
          label: t('Position Wise'),
          value: 'Position',
        },
      ],
    },
    {
      label: t('Project'),
      key: 'project',
      type: 'project',
      colspan: 2,
    },
    {
      label: `${t('Start Date')} (${t('optional')})`,
      key: 'startDate',
      type: 'date',
    },
    {
      label: `${t('End Date')} (${t('optional')})`,
      key: 'endDate',
      type: 'date',
    },
  ];

  const validationSchema = Yup.object().shape({
    project: Yup.object().shape({
      id: Yup.string().required(t('Is required')),
    }),
    // Dates are optional - when empty, entire time range is used
  });

  const isChrMode = dataCollection.billingMode === 'chr';

  // Work package selection logic (for positions mode)
  const [allWorkPackageIds, setAllWorkPackageIds] = useState([]);
  function countWorkPackages() {
    const wpIds = [];
    apiData?.data.forEach((position) => {
      const { workPackages } = position;
      workPackages.forEach((wp) => {
        wpIds.push(wp.id);
      });
    });
    return wpIds;
  }
  useEffect(() => {
    if (apiData) setAllWorkPackageIds(countWorkPackages());
  }, [apiData]);

  // Wage preset selection logic (for CHR mode)
  const [allWagePresetIds, setAllWagePresetIds] = useState([]);
  useEffect(() => {
    if (wagePresetsData?.data) {
      // Only include presets that have open (unbilled) time
      const presetIds = wagePresetsData.data
        .filter(
          (wp) => (wp.workedTimeSeconds || 0) - (wp.billedTimeSeconds || 0) > 0,
        )
        .map((wp) => wp.id);
      setAllWagePresetIds(presetIds);
    }
  }, [wagePresetsData]);

  const SelectedIcon = isChrMode
    ? selectedWagePresetIds?.length === allWagePresetIds?.length
      ? CheckBoxIcon
      : selectedWagePresetIds?.length === 0
        ? CheckBoxOutlineBlankIcon
        : IndeterminateCheckBoxIcon
    : workPackageIds?.length === allWorkPackageIds?.length
      ? CheckBoxIcon
      : workPackageIds?.length === 0
        ? CheckBoxOutlineBlankIcon
        : IndeterminateCheckBoxIcon;

  function handleMainSelect(e) {
    if (isChrMode) {
      // For CHR mode
      if (selectedWagePresetIds.length === allWagePresetIds.length) {
        return setSelectedWagePresetIds([]);
      }
      return setSelectedWagePresetIds(allWagePresetIds);
    } else {
      // For positions mode
      if (workPackageIds.length === allWorkPackageIds.length) {
        return setWorkPackageIds([]);
      }
      return setWorkPackageIds(allWorkPackageIds);
    }
  }

  function handleWagePresetSelect(presetId) {
    if (selectedWagePresetIds.includes(presetId)) {
      setSelectedWagePresetIds(
        selectedWagePresetIds.filter((id) => id !== presetId),
      );
    } else {
      setSelectedWagePresetIds([...selectedWagePresetIds, presetId]);
    }
  }

  // Get selected presets for review step
  const getSelectedPresets = () => {
    return (
      wagePresetsData?.data?.filter((wp) =>
        selectedWagePresetIds.includes(wp.id),
      ) || []
    );
  };

  // Render review step for CHR mode
  if (step === 'review') {
    const startDate = dataCollection.startDate || '1970-01-01';
    const endDate = dataCollection.endDate || moment().format('YYYY-MM-DD');

    return (
      <TimeEntryReviewStep
        selectedPresets={getSelectedPresets()}
        startDate={startDate}
        endDate={endDate}
        onBack={() => setStep('selection')}
        onConfirm={handleReviewConfirm}
        config={config}
      />
    );
  }

  // Render review step for Positions mode
  if (step === 'positionsReview') {
    const startDate = dataCollection.startDate || '1970-01-01';
    const endDate = dataCollection.endDate || moment().format('YYYY-MM-DD');

    return (
      <TimeEntryReviewStep
        selectedPresets={buildPositionsPresets()}
        startDate={startDate}
        endDate={endDate}
        onBack={() => setStep('selection')}
        onConfirm={handlePositionsReviewConfirm}
        config={config}
        mode="positions"
      />
    );
  }

  return (
    <Box
      className="flex flex-col gap-2 h-full justify-between"
      sx={{ '& .group': { display: 'block!important' } }}
    >
      <Loader active={isLoading} />
      <Box className="flex flex-col gap-4 overflow-y-auto">
        <Box className="flex justify-center items-center my-2 px-2">
          <Typography fontSize={22}>{t('Time Billing')}</Typography>
        </Box>
        <Box className="grid grid-cols-2 gap-4">
          <FormFields
            fields={fields}
            updateDataCollection={handleChange}
            onValidationChange={setIsValid}
            validationSchema={validationSchema}
            dataCollection={dataCollection}
          />
        </Box>
        <Box className="flex flex-col gap-2">
          {isChrMode ? (
            // CHR Mode - Show wage presets
            <>
              {wagePresetsData && wagePresetsData.data.length > 0 && (
                <Box className="flex">
                  <Box
                    className="flex items-center gap-2 pl-2 cursor-pointer"
                    onClick={handleMainSelect}
                  >
                    <IconButton onClick={handleMainSelect}>
                      {SelectedIcon && <SelectedIcon />}
                    </IconButton>
                    <Typography
                      fontWeight={500}
                      fontSize={12}
                      className="opacity-50"
                    >
                      {t('All Elements')}
                    </Typography>
                  </Box>
                </Box>
              )}
              {wagePresetsData && wagePresetsData.data.length > 0 ? (
                <Box className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {wagePresetsData.data.map((preset) => {
                    const workedSeconds = preset.workedTimeSeconds || 0;
                    const billedSeconds = preset.billedTimeSeconds || 0;
                    const workedHours = workedSeconds / 3600;
                    const billedHours = billedSeconds / 3600;
                    const openHours = Math.max(0, workedHours - billedHours);
                    const openAmount = openHours * (preset.wage || 0);
                    const isSelected = selectedWagePresetIds.includes(
                      preset.id,
                    );
                    const hasOpenTime = openHours > 0;

                    return (
                      <Box
                        key={preset.id}
                        className={`relative flex p-3 items-start gap-2 cursor-pointer transition-all ${
                          !hasOpenTime ? 'opacity-50' : ''
                        }`}
                        onClick={() =>
                          hasOpenTime && handleWagePresetSelect(preset.id)
                        }
                        sx={{
                          backgroundColor: isSelected
                            ? preset.color + '50'
                            : preset.color + '20',
                          borderRadius: 2,
                          border: `2px solid ${isSelected ? preset.color : preset.color + '60'}`,
                          '&:hover': hasOpenTime
                            ? {
                                backgroundColor: preset.color + '40',
                              }
                            : {},
                        }}
                      >
                        <Box className="flex items-center">
                          <IconButton size="small" disabled={!hasOpenTime}>
                            {isSelected ? (
                              <CheckBoxIcon sx={{ color: preset.color }} />
                            ) : (
                              <CheckBoxOutlineBlankIcon />
                            )}
                          </IconButton>
                        </Box>
                        {preset.image && (
                          <img
                            className="w-[32px] h-[32px] object-contain"
                            src={preset.image.temporaryUrl}
                            alt={preset.label}
                          />
                        )}
                        <Box className="flex-1">
                          <Typography fontWeight={600}>
                            {preset.label}
                          </Typography>
                          <Box className="flex justify-between gap-2">
                            <Typography>
                              <MoneyLabel money={preset.wage} /> / {t('h')}
                            </Typography>
                          </Box>
                          <Box className="flex pt-1 gap-4">
                            <UserStack users={preset.users || []} />
                          </Box>
                        </Box>
                        <Box className="flex flex-col items-end gap-1">
                          <Box className="flex items-center gap-2">
                            <Typography fontSize={12} className="opacity-60">
                              {t('Open')}:
                            </Typography>
                            <Typography fontWeight={600} fontSize={14}>
                              {renderHours(openHours * 60) || '0:00'} {t('h')}
                            </Typography>
                          </Box>
                          <Box className="flex items-center gap-2">
                            <Typography fontWeight={600} fontSize={14}>
                              <MoneyLabel money={openAmount} />
                            </Typography>
                          </Box>
                          <Box className="flex items-center gap-2">
                            <Typography fontSize={12} className="opacity-60">
                              {t('Worked')}:
                            </Typography>
                            <Typography fontSize={12} fontWeight={600}>
                              {renderHours(workedHours * 60) || '0:00'} {t('h')}
                            </Typography>
                          </Box>
                          <Box className="flex items-center gap-2">
                            <Typography fontSize={12} className="opacity-60">
                              {t('Billed')}:
                            </Typography>
                            <Typography fontSize={12} fontWeight={600}>
                              {renderHours(billedHours * 60) || '0:00'} {t('h')}
                            </Typography>
                          </Box>
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
              ) : (
                <Box className="flex justify-center mt-2">
                  <NoDataAnimation />
                </Box>
              )}
            </>
          ) : (
            // Positions Mode - Show billable positions
            <>
              {apiData && apiData.data.length > 0 && (
                <Box className="flex">
                  <Box
                    className="flex items-center gap-2 pl-2 cursor-pointer"
                    onClick={handleMainSelect}
                  >
                    <IconButton onClick={handleMainSelect}>
                      {SelectedIcon && <SelectedIcon />}
                    </IconButton>
                    <Typography
                      fontWeight={500}
                      fontSize={12}
                      className="opacity-50"
                    >
                      {t('All Elements')}
                    </Typography>
                  </Box>
                </Box>
              )}
              {apiData && apiData.data.length > 0 ? (
                apiData.data.map((position) => (
                  <BillingPosition
                    key={position.id}
                    position={position}
                    onSelectionChange={setWorkPackageIds}
                    selection={workPackageIds}
                  />
                ))
              ) : (
                <Box className="flex justify-center mt-2">
                  <NoDataAnimation />
                </Box>
              )}
            </>
          )}
        </Box>
      </Box>
      <Box className="flex justify-between p-4">
        <Button
          color="error"
          variant="outlined"
          onClick={onClose}
          sx={{ visibility: onClose ? 'visible' : 'hidden' }}
        >
          {t('Cancel')}
        </Button>

        <Button
          color="success"
          variant="contained"
          disabled={
            (isChrMode
              ? selectedWagePresetIds.length === 0
              : workPackageIds.length === 0) || !isValid
          }
          onClick={handleSubmit}
        >
          {isChrMode ? t('Next') : t('Generate Positions')}
        </Button>
      </Box>
    </Box>
  );
};

export default SelectTimeBilling;
